import AppContext from 'App/AppContext';
import { IPIconButton, IPModal, IPPivot, IPSeparator, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ChecklistContainer } from 'components/Checklist/ChecklistContainer';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { cancelIcon, globalStackStylesHeight100, globalStackTokensGapSmall } from 'globalStyles';
import Task from 'models/tasks/task';
import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from 'utils/string';
import {
  getCompactViewSwitch,
  getTaskViewModalStackStyles,
  getTaskViewModalStyles,
  useCompactViewPivotRemountKey,
} from '../View/TaskRenderHelpers';
import { TaskDetailOwner } from './Details/TaskDetailOwner';
import { NormalTaskDetail } from './NormalTaskDetail';
import { INormalTaskLayoutProps } from './NormalTaskLayoutProps';
import { TaskAIPane } from './TaskAIPane';

// The classic task dialog: title and owner on top, the details beside a pivot (the details are a tab in the
// compact view). Retiring the classic view means deleting this file and its branch in NormalTask.
export const NormalTaskClassicLayout = (props: INormalTaskLayoutProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const taskProps = props.taskProps;
  const pivotRemountKey = useCompactViewPivotRemountKey(taskProps.compactView);
  // With a side pane the two task columns share the room that is left; the compact view already drops
  // the details column, so the pivot takes what the pane leaves.
  const hasSidePane = taskProps.sidePane !== undefined;
  const detailsColumnWidth = hasSidePane ? '35%' : '50%';
  const pivotColumnWidth = taskProps.compactView ? (hasSidePane ? '70%' : '100%') : hasSidePane ? '35%' : '50%';

  const getTaskDetails = () => {
    return (
      <NormalTaskDetail
        task={taskProps.task}
        orgTask={taskProps.orgTask}
        onUpdate={(newTask: Task) => {
          taskProps.onUpdateTask(newTask);
        }}
        taskStates={taskProps.task.taskStates!}
        getErrorMessage={props.validate}
        isActionPending={taskProps.isActionPending}
        loadRescheduleRange={taskProps.loadRescheduleRange}
        tags={taskProps.tags}
        addTagToTaskState={taskProps.addTagToTaskState}
        removeTagFromTaskState={taskProps.removeTagFromTaskState}
        onChangeCompletionDate={taskProps.onChangeCompletionDate}
        canUpdate={taskProps.canUpdate}
        compactView={taskProps.compactView}
      />
    );
  };

  return (
    <IPModal
      forceFocusInsideTrap={false}
      isOpen={taskProps.isOpen}
      styles={getTaskViewModalStyles(taskProps.windowLevel, false)}
    >
      {taskProps.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, taskProps.compactView, hasSidePane)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between" styles={{ root: { minWidth: 0 } }}>
            <IPStack.Item styles={{ root: { minWidth: 0 } }}>
              {taskProps.task.taskId === -1 && (
                <IPText block nowrap variant="xxLarge">
                  {t('task:Title.Normal.New')}
                </IPText>
              )}
              {taskProps.task.taskId !== -1 && taskProps.compactView && (
                <IPTooltipHost content={taskProps.task.name}>
                  <IPText block nowrap variant="xxLarge">
                    {capitalizeFirstLetter(taskProps.task.name)}
                  </IPText>
                </IPTooltipHost>
              )}
              {taskProps.task.taskId !== -1 && !taskProps.compactView && (
                <IPText variant="xxLarge">
                  {!taskProps.task.isSeries() && t('task:Title.Normal.Edit')}
                  {taskProps.task.isSeries() && t('task:Title.Normal.Series.Edit')}
                </IPText>
              )}
            </IPStack.Item>
            {!taskProps.compactView && <IPStack.Item>{props.renderTopBar(false)}</IPStack.Item>}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={props.onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={taskProps.orgTask}
              task={taskProps.task}
              onUpdate={taskProps.onUpdateTask}
              canUpdate={taskProps.canUpdate}
            />
          </IPStack.Item>
          <IPSeparator />
          {props.renderMessages(() => taskProps.setSelectedPivot('Context'))}
        </IPStack.Item>
        {taskProps.compactView && <IPStack.Item>{props.renderTopBar(true)}</IPStack.Item>}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!taskProps.compactView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: detailsColumnWidth, minWidth: 0 } }}>
                  {getTaskDetails()}
                </IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={{ root: { width: pivotColumnWidth, minWidth: 0 } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPStack horizontal={!appContext.isMobileView} verticalAlign="start">
                    {getCompactViewSwitch(
                      taskProps.task,
                      appContext.isMobileView,
                      taskProps.compactView,
                      t,
                      taskProps.setCompactView,
                      appContext,
                    )}
                    <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
                      <IPPivot
                        key={pivotRemountKey}
                        selectedKey={taskProps.selectedPivot}
                        onLinkClick={(item) => taskProps.setSelectedPivot(item?.props.itemKey ?? '')}
                        overflowBehavior="menu"
                      >
                        {props.renderPivotItem('TaskDetails')}
                        {props.renderPivotItem('TaskType')}
                        {props.renderPivotItem('Checklist')}
                        {props.renderPivotItem('Context')}
                        {props.renderPivotItem('Resources')}
                        {props.renderPivotItem('Activities')}
                        {props.renderPivotItem('Instances')}
                        {props.renderPivotItem('Audit')}
                      </IPPivot>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
                {taskProps.selectedPivot === 'TaskDetails' && <IPStack.Item grow>{getTaskDetails()}</IPStack.Item>}
                {props.renderTaskTypeForm(taskProps.selectedPivot === 'TaskType', taskProps.compactView)}
                {taskProps.selectedPivot === 'Checklist' && <ChecklistContainer {...props.checklistProps} />}
                {props.renderPivotPanels(taskProps.selectedPivot)}
              </IPStack>
            </IPStack.Item>
            {/* AI assistant column, opened by the criterion path or the AI button */}
            {hasSidePane && <TaskAIPane>{taskProps.sidePane}</TaskAIPane>}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{props.renderFooter()}</IPStack.Item>
      </IPStack>
      {props.children}
    </IPModal>
  );
};
